import React, { Component, PropTypes } from 'react';

import { getColour } from '../../../components/charts/highcharts-themes/helper';

import Breakpoint from '../../../utils/breakpoints';

import styled, { css } from 'styled-components';
import { media } from 'style-utils';

const DONUT_WIDTH = 40;
const MAX_DOTS = 20;

const ArcRing = css`
  content: '';
  width: 100%;
  height: 100%;
  position: absolute;
  transform-origin: 50% 0%;
  left: 0;
`;

const GaugeContainer = styled.section`
  padding: 0 10%;
  margin-bottom: 10px;

  ${media.mobile`
    margin-bottom: 0;
    padding: 0;
  `}
`;

const Info = styled.div`
  display: flex;
  justify-content: space-around;
  text-align: center;
  padding: 0;
  width: 120%;
  margin-left: -10%;

  ${media.mobile`
    flex-direction: column;
    padding: 0;
    text-align: left;
  `}
`;

const InfoContent = styled.div`
  width: 33.33%;
  font-size: 12px;
  font-family: ${props => props.theme.font.bold};

  color: ${props => props.color};

  ${media.mobile`
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    line-height: 18px;
    width: 100%;
    max-width: 200px;
    margin: 0 auto;
  `}
`;
const InfoTrend = styled.div`
  font-family: ${props => props.theme.font.default};

  ${media.mobile`
    font-size: 12px;
    font-family: ${props => props.theme.font.bold};
  `}
`;

const DotWrapper = styled.div`
  margin: 10px auto 30px;
  max-width: 200px;

  ${media.extraSmallMobile`
    margin: 0 auto 5px;
  `}

  ${media.averageMobile`
    margin-bottom: 10px;
  `}
`;

const DotRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
`;

const Dot = styled.div`
  background-color: ${props => props.color};
  border-radius: 50%;
  height: 8px;
  margin: 3px;
  width: 8px;

  ${media.averageMobile`
    height: 5px;
    margin: 2px;
    width: 5px;
  `}

  ${media.extraSmallMobile`
    height: 5px;
    margin: 2px;
    width: 5px;
  `}
`;

const CanvasWrapper = styled.div`
  position: relative;
  width: 100%;
  &:before {
    content: '';
    display: block;
    padding-top: 50%;
  }
`;

const Canvas = styled.canvas`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
`;

class Gauge extends Component {
  constructor(props) {
    super(props);
    this.state = {
      height: 200
    };
  }
  componentDidMount() {
    window.addEventListener('resize', this.update, false);
    this.update();
  }

  shouldComponentUpdate(nextProps, nextState) {
    return this.state.height !== nextState;
  }

  componentDidUpdate() {
    this.draw();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.update, false);
  }

  update = () => {
    const radius = this.canvasWrapper.clientHeight > 0 ? this.canvasWrapper.clientHeight : 30;
    this.setState({
      height: radius
    });
  };

  draw = () => {
    const { values, total, themes } = this.props;

    const radius = this.state.height;
    this.canvas.height = radius;
    this.canvas.width = 2 * radius;

    // Draw base arc
    this.drawArc('#707070', 0, 1);

    let reducer = 0;
    values.map((trend, index) => {
      this.drawArc(themes[index], reducer / total, (reducer + trend.value) / total);
      reducer += trend.value;
      return null;
    });
  };

  drawArc = (style, start, end) => {
    const ctx = this.canvas.getContext('2d');
    const radius = this.state.height;

    ctx.beginPath();
    ctx.arc(radius, radius, radius - DONUT_WIDTH / 2, Math.PI * (1 + start), Math.PI * (1 + end));
    ctx.strokeStyle = style;
    ctx.lineWidth = DONUT_WIDTH;
    ctx.stroke();
    ctx.closePath();
  };

  render() {
    return (
      <CanvasWrapper innerRef={ref => (this.canvasWrapper = ref)}>
        <Canvas data-paper-resize innerRef={ref => (this.canvas = ref)} />
      </CanvasWrapper>
    );
  }
}

Gauge.propTypes = {
  values: PropTypes.array,
  total: PropTypes.number,
  themes: PropTypes.array
};

class InsightGauge extends Component {
  renderDots(themes) {
    const { values } = this.props;

    if (!values) {
      return null;
    }

    const total = values.reduce((acc, trend) => acc += parseInt(trend.Value, 10), 0);

    // Dot value
    const dotValue = 1 / MAX_DOTS;
    const dots = [];
    let largestTrend = 0;

    // values.sort((a, b) => {
    //   return parseInt(a.Value, 10) - parseInt(b.Value, 10);
    // });

    values.forEach((trend, index) => {
      const trendDots = total > MAX_DOTS ?
        MAX_DOTS * (trend.Value / total) : trend.Value;

      // Keep a record of the largest trend
      largestTrend = trend.Value > largestTrend ? trend.Value : largestTrend;

      // If the trend has a value but it's too low, give it a single dot
      if (trend.Value / total < dotValue && trend.Value && trend.Value !== '0') {
        dots.push(themes[index]);

        return;
      }

      for (let i = 0; i < Math.floor(trendDots); i++) {
        dots.push(themes[index]);
      }
    });

    if (dots.length > MAX_DOTS) {
      dots.splice(dots.indexOf(largestTrend), 1);
    }

    if (dots.length < MAX_DOTS / 2) {
      return (
        <DotRow>
          {dots.map((color, i) => (
            <Dot
              key={`dot-${color}-${i}`}
              color={color}
            />
          ))}
        </DotRow>
      );
    }

    const rowBreakingDot = Math.ceil(dots.length / 2);
    const rows = [
      dots.splice(0, rowBreakingDot),
      dots.splice(0, rowBreakingDot),
    ];

    return rows.map((row, index) => (
      <DotRow key={`dot-row-${index}`}>
        {row.map((color, i) => (
          <Dot
            key={`dot-${color}-${i}`}
            color={color}
          />
        ))}
      </DotRow>
    ));
  }

  render() {
    const { description } = this.props;

    const values = this.props.values.map(trend => ({
      value: Number(trend.Value),
      description: trend.Description
    }));

    const total = values.reduce((acc, cur) => acc + cur.value, 0);

    // let reducer = 0;

    const themes = getColour(values.length);

    return (
      <GaugeContainer title={description}>
        <Breakpoint name="tabletDesktop">
          <Gauge values={values} themes={themes} total={total} />
        </Breakpoint>
        <Breakpoint name="mobile">
          <DotWrapper>
            {this.renderDots(themes)}
          </DotWrapper>
        </Breakpoint>
        <Info>
          {values.map((trend, index) => (
            <InfoContent key={index} color={themes[index]}>
              {trend.value}
              <InfoTrend>{trend.description}</InfoTrend>
            </InfoContent>
          ))}
        </Info>
      </GaugeContainer>
    );
  }
}

InsightGauge.propTypes = {
  values: PropTypes.array,
  delta: PropTypes.number,
  trendType: PropTypes.string,
  dataType: PropTypes.string,
  description: PropTypes.string
};

export default InsightGauge;



// WEBPACK FOOTER //
// ./src/features/insights/components/InsightGauge.js